// NodeViewer.js
import React from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Tooltip,
  Alert,
  AlertTitle,
} from "@mui/material";
import { buildNumberedList } from "./nodeUtils";

/**
 * NodeViewer
 * Props:
 *  - root: the tree root
 *  - nodeConfigs: used to format templates
 *  - onSelectNode(id) - called when clicking the rule number
 *  - selectedNodeId - currently selected node id
 */
const NodeViewer = ({
  root,
  selectedCategory = null,
  nodeConfigs = {},
  onSelectNode,
  selectedNodeId,
}) => {
  const lines = buildNumberedList(root, nodeConfigs);
  return (
    <Paper
      sx={{
        p: 1.5,
        mb: 1.5,
        background: "#f9f9ff",
        border: "1px solid #e0e0e0",
      }}
      elevation="1"
    >
      <Box sx={{ mb: 2, p: 1 }}>
        {lines.map((line) => {
          if (line.isOutcomeHeader) {
            return (
              <Box
                key={`viewer-header-${line.id}`}
                sx={{ ml: line.depth * 2, my: 0.5 }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "#6c63ff", fontWeight: 600 }}
                >
                  {line.text}
                </Typography>
              </Box>
            );
          }
          // normal rule line
          const isSelected = selectedNodeId === line.id;
          return (
            <Box
              key={`viewer-${line.id}`}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                ml: Math.max(0, line.depth) * 2,
                py: 0.5,
              }}
            >
              <Tooltip title="Click to focus this rule in the editor" arrow>
                <Button
                  size="small"
                  onClick={() =>
                    onSelectNode &&
                    onSelectNode({ id: line.id, numbering: line.numbering })
                  }
                  variant={isSelected ? "contained" : "outlined"}
                  sx={{
                    minWidth: 32,
                    height: 28,
                    fontSize: "0.8rem",
                    lineHeight: 1,
                    flexShrink: 0,
                  }}
                >
                  Rule - {line.numbering || "•"}
                </Button>
              </Tooltip>

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.9rem",
                    color: isSelected ? "#1e88e5" : "inherit",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  {line.node?.node === "Category Check"
                    ? `Category - ${selectedCategory}`
                    : line.node?.node}
                  &nbsp; - {line.text}
                </Typography>

                {/* Display error information if present */}
                {line.node?.error && (
                  <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                    <AlertTitle sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                      Error
                    </AlertTitle>
                    <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                      {line.node.error}
                    </Typography>
                  </Alert>
                )}

                {/* Display traceback if present */}
                {line.node?.traceback?.traceback && (
                  <Box
                    sx={{
                      mt: 1,
                      p: 1.5,
                      backgroundColor: "#fff3e0",
                      border: "1px solid #ff9800",
                      borderRadius: 1,
                      maxWidth: "100%",
                      overflow: "auto",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: "#e65100",
                        display: "block",
                        mb: 0.5,
                      }}
                    >
                      Traceback:
                    </Typography>
                    <Typography
                      component="pre"
                      variant="body2"
                      sx={{
                        fontSize: "0.75rem",
                        fontFamily: "monospace",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        color: "#d84315",
                        margin: 0,
                      }}
                    >
                      {line.node.traceback.traceback}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

export default NodeViewer;
